import { styled } from "@linaria/react"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { Navigate } from "@tanstack/react-router"
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools"
import { observer } from "mobx-react-lite"

import { Centered } from "@/components/Centered/Centered.ts"
import { Gap, Vertical } from "@/components/Flex.ts"
import { GlobalLoader } from "@/components/Loader/GlobalLoader.tsx"
import { useRootContext } from "@/hooks/useRootContext.ts"
import { GithubFill, Icon32 } from "@/icons/Icons.ts"
import { MatterLogoAndName } from "@/routes/auth/MatterLogoAndName.tsx"
import { PageBackgroundGradient } from "@/routes/auth/PageBackgroundGradient.ts"
import { PageBackground, PageContainer, PageContent } from "@/routes/auth/PageStyles.ts"
import { Navigator } from "@/routes/Navigator.ts"

import GoogleLogo from "./google.svg?react"
import JetBrainsLogo from "./jetbrains.svg?react"

export const LoginScene = observer(() => {
  const context = useRootContext()

  if (context.authController.authenticated.get()) {
    return <Navigate {...Navigator.home()} replace />
  }

  const isInIframe = window.self !== window.top

  if (isInIframe) {
    return (
      <>
        <PageContainer>
          <PageContent twoPaneLayout={false}>
            <StyledCentered>
              <p>
                Please open this page in a <a href={Navigator.login().to} target="_blank" rel="noopener noreferrer">new
                tab</a> to sign in.
              </p>
              <p>Authentication is not supported inside an iframe.</p>
            </StyledCentered>
          </PageContent>
        </PageContainer>

        <PageBackground background={PageBackgroundGradient.Home} />
      </>
    )
  }

  return (
    <>
      {context.useLoader && <GlobalLoader />}

      <PageContainer>
        <PageContent twoPaneLayout={false}>
          <StyledCentered vertical={false}>
            <MatterLogoAndName />
            <Gap height="16px" />
            <Vertical gap="10px">
              <WelcomeHeading>
                Welcome to Matter
              </WelcomeHeading>
              <Slogan>AI development companion for product teams</Slogan>
            </Vertical>
            <Gap height="48px" />
            <Vertical gap="16px">
              <>
                <AuthProvider href="#" onClick={(e) => {
                  e.preventDefault()
                  context.authController.signIn("Google")
                }}>
                  <GoogleLogo />
                  Continue with Google
                </AuthProvider>

                <AuthProvider href="#" onClick={(e) => {
                  e.preventDefault()
                  context.authController.signIn("GitHub")
                }}>
                  <GithubFill className={Icon32} color="var(--text-color)" />
                  Continue with GitHub
                </AuthProvider>
              </>

              <AuthProvider href="#" onClick={(e) => {
                e.preventDefault()
                context.authController.signIn(null)
              }}>
                <JetBrainsLogo />
                Continue with JetBrains
              </AuthProvider>
            </Vertical>
          </StyledCentered>
        </PageContent>
      </PageContainer>

      <PageBackground background={PageBackgroundGradient.Orgs} />

      {window.parent === window && import.meta.env.DEV && <TanStackRouterDevtools position="bottom-right" />}
      {window.parent === window && import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </>
  )
})

const StyledCentered = styled(Centered)`
  view-transition-name: login;

  padding-top: 128px;
  text-align: center;
`

const WelcomeHeading = styled.h1`
  max-width: 600px;
  margin: 0;

  font-size: 54px;
  font-weight: 600;
  line-height: 60px;
  letter-spacing: -0.036px;

  text-align: center;
`

const Slogan = styled.h2`
  font-size: 18px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: -0.036px;
  margin: 0;
`

const AuthProvider = styled.a`
  display: flex;
  align-items: center;
  gap: 16px;

  max-width: 400px;
  padding: 16px 24px;

  border-radius: var(--border-radius-16);
  box-shadow: 0 2px 20px 0 oklch(37.6% 0.183 267.561deg / 5%);

  color: var(--text-color);
  background: var(--background-color);

  font-size: 18px;
  font-weight: 500;
  line-height: 24px;
`
